import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  Award,
  Settings,
  ShieldCheck,
  Headphones,
  Sun,
  FileText,
  Zap,
  MessageCircle,
} from "lucide-react";
import { getWhatsAppLink } from "../lib/config";
import { CTASection } from "../components/CTASection";

const STATS = [
  { value: "10+", label: "Anni di Esperienza" },
  { value: "200+", label: "Progetti Completati" },
  { value: "2000+", label: "kW Installati" },
  { value: "150+", label: "Clienti Soddisfatti" },
];

const WHY = [
  {
    icon: Award,
    title: "Esperienza Certificata",
    text: "Oltre 10 anni nel settore fotovoltaico e Conto Termico.",
  },
  {
    icon: Settings,
    title: "Gestione Completa",
    text: "Dalla progettazione all'attivazione GSE, senza pensieri.",
  },
  {
    icon: ShieldCheck,
    title: "Qualità Garantita",
    text: "Certificazioni e conformità normativa assicurate.",
  },
  {
    icon: Headphones,
    title: "Supporto Dedicato",
    text: "Assistenza personalizzata in ogni fase del progetto.",
  },
];

const SERVICES = [
  {
    icon: Sun,
    title: "Progettazione Impianti",
    text:
      "Progettazione completa di impianti fotovoltaici residenziali e commerciali fino a 1000 kW.",
    points: [
      "Analisi di fattibilità tecnica ed economica",
      "Dimensionamento ottimale dell'impianto",
      "Scelta dei componenti più efficienti",
    ],
  },
  {
    icon: FileText,
    title: "Gestione delle Pratiche",
    text:
      "Gestione completa di tutte le pratiche amministrative e burocratiche — servizio principale.",
    points: [
      "Domanda di connessione E-Distribuzione",
      "Pratiche comunali e autorizzazioni",
      "Convenzione GSE (SSP/RID) e Conto Termico",
    ],
    featured: true,
  },
  {
    icon: Zap,
    title: "Progettazione Elettrica",
    text: "Progettazione di impianti elettrici civili, industriali e commerciali.",
    points: [
      "Progettazione impianti elettrici B.T.",
      "Quadri elettrici certificati",
      "Dimensionamento impianti",
    ],
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page" className="min-h-screen bg-slate-950 text-white">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80"
            alt="Pannelli fotovoltaici"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/40 to-slate-950" />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 text-center py-32">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-medium text-emerald-300 tracking-wider uppercase">
              Ingegnere Elettrico — Fotovoltaico & Conto Termico
            </span>
          </div>
          <h1
            data-testid="hero-title"
            className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight mb-6 leading-[1.05]"
          >
            <span className="block text-white">Energia Solare</span>
            <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-sky-400 bg-clip-text text-transparent">
              per il Futuro
            </span>
          </h1>
          <p className="text-base lg:text-xl text-slate-300 max-w-2xl mx-auto mb-10">
            Progettazione e gestione completa di impianti fotovoltaici con oltre 10+ anni di
            esperienza. Pratiche GSE, E-Distribuzione e Conto Termico 2.0/3.0 a partire da 250€.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 font-semibold text-base hover:shadow-2xl hover:shadow-emerald-500/40 hover:scale-105 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Richiedi Consulenza
            </a>
            <Link
              to="/servizi"
              data-testid="hero-cta-services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/30 bg-white/5 backdrop-blur-md text-white font-semibold text-base hover:bg-white/10 hover:border-white/50 transition-all"
            >
              Scopri i Servizi
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce text-emerald-400/70">
          <ChevronDown className="w-6 h-6" />
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="py-20 lg:py-24 bg-slate-950 relative">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <div
                key={i}
                data-testid={`stat-card-${i}`}
                className="text-center p-8 rounded-3xl border border-white/5 bg-gradient-to-br from-white/[0.03] to-transparent hover:border-emerald-400/30 transition"
              >
                <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent mb-2">
                  {s.value}
                </div>
                <div className="text-sm text-slate-400 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section data-testid="why-section" className="py-24 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-semibold tracking-[0.2em] text-emerald-400 uppercase mb-3">
              Perché sceglierci
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-5 leading-tight">
              Perché Scegliere <span className="text-emerald-400">Ing. Vasta</span>
            </h2>
            <p className="text-base text-slate-400">
              Professionalità, esperienza e supporto completo in ogni fase del progetto.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <div
                  key={i}
                  data-testid={`why-card-${i}`}
                  className="group p-7 rounded-3xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-emerald-400/30 transition-all"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{w.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="py-24 lg:py-32 bg-slate-900/40 relative">
        <div className="max-w-7xl mx-auto px-5">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-semibold tracking-[0.2em] text-emerald-400 uppercase mb-3">
              Cosa facciamo
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-5 leading-tight">
              I Nostri <span className="text-emerald-400">Servizi</span>
            </h2>
            <p className="text-base text-slate-400">
              Soluzioni complete per ogni esigenza fotovoltaica.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  data-testid={`service-card-${i}`}
                  className={`relative p-8 rounded-3xl border transition-all ${
                    s.featured
                      ? "border-emerald-400/40 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 shadow-lg shadow-emerald-500/10"
                      : "border-white/5 bg-white/[0.02] hover:border-emerald-400/20"
                  }`}
                >
                  {s.featured && (
                    <span className="absolute top-5 right-5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold tracking-wider uppercase">
                      Principale
                    </span>
                  )}
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30">
                    <Icon className="w-7 h-7 text-slate-950" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{s.title}</h3>
                  <p className="text-sm text-slate-400 mb-5 leading-relaxed">{s.text}</p>
                  <ul className="space-y-2 mb-6">
                    {s.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                        <span className="text-emerald-400 mt-1">✓</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/servizi"
              data-testid="view-all-services-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-emerald-400/30 text-emerald-400 font-semibold text-sm hover:bg-emerald-500/10 transition-all"
            >
              Vedi Tutti i Servizi
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
